import { z } from "zod";
import { IsoDateTime } from "../common.ts";
import { ConsoleRole, OperatorPermission } from "../ports.ts";
import { OpaqueToken } from "./auth.ts";

// Console user management routes of the Explorer API (`iam`, design D4): `users.manage` on the
// operator the request names; a user or invitation outside it answers `404`.

/** The operator an Administrator acts on (query of the list routes). */
export const OperatorScopeQueryV1 = z
  .object({ operator_id: z.uuid() })
  .strict()
  .meta({ id: "OperatorScopeQueryV1" });

/** Body of the per-user actions (deactivate, unlock, password-setup invitation, revoke). */
export const OperatorScopedRequestV1 = z
  .object({ operator_id: z.uuid() })
  .strict()
  .meta({ id: "OperatorScopedRequestV1" });
export type OperatorScopedRequestV1 = z.infer<typeof OperatorScopedRequestV1>;

/**
 * Kind of a console invitation: a new member (`account`), a new password for an existing user
 * (`password_setup`), a new Platform Administrator, or the recovery of an existing user whose
 * two-factor authentication was reset (`two_factor_recovery`: a new password and a new
 * authenticator; change add-console-account-settings, design D7).
 */
export const ConsoleInvitationKindV1 = z
  .enum(["account", "password_setup", "platform_administrator", "two_factor_recovery"])
  .meta({ id: "ConsoleInvitationKindV1" });
export type ConsoleInvitationKindV1 = z.infer<typeof ConsoleInvitationKindV1>;

export const ConsoleUserStatusV1 = z
  .enum(["active", "locked", "deactivated"])
  .meta({ id: "ConsoleUserStatusV1" });

/** A console user of the operator (email shown only to `users.manage` holders; never logged). */
export const ConsoleUserSummaryV1 = z
  .object({
    console_user_id: z.uuid(),
    email: z.email(),
    status: ConsoleUserStatusV1,
    role: ConsoleRole.nullable(),
    permissions: z.array(OperatorPermission),
    locked_for_seconds: z.int().min(0),
  })
  .strict()
  .meta({ id: "ConsoleUserSummaryV1" });
export type ConsoleUserSummaryV1 = z.infer<typeof ConsoleUserSummaryV1>;

export const ConsoleUserListResponseV1 = z
  .object({ console_users: z.array(ConsoleUserSummaryV1) })
  .strict()
  .meta({ id: "ConsoleUserListResponseV1" });
export type ConsoleUserListResponseV1 = z.infer<typeof ConsoleUserListResponseV1>;

/** `POST /console-users`: invites a colleague with one role on the operator. */
export const InviteConsoleUserRequestV1 = z
  .object({ operator_id: z.uuid(), email: z.email().max(254), role: ConsoleRole })
  .strict()
  .meta({ id: "InviteConsoleUserRequestV1" });
export type InviteConsoleUserRequestV1 = z.infer<typeof InviteConsoleUserRequestV1>;

/**
 * A single-use invitation (72 h). `invitation_token` is returned once, to build the link the
 * Administrator hands over through a corporate channel; only its digest is stored.
 */
export const InvitationIssuedResponseV1 = z
  .object({
    invitation_id: z.uuid(),
    kind: ConsoleInvitationKindV1,
    invitation_token: OpaqueToken,
    expires_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "InvitationIssuedResponseV1" });
export type InvitationIssuedResponseV1 = z.infer<typeof InvitationIssuedResponseV1>;

/** `PUT /console-users/{id}/roles`: sets the role on the operator; `null` removes the access. */
export const SetConsoleUserRoleRequestV1 = z
  .object({ operator_id: z.uuid(), role: ConsoleRole.nullable() })
  .strict()
  .meta({ id: "SetConsoleUserRoleRequestV1" });
export type SetConsoleUserRoleRequestV1 = z.infer<typeof SetConsoleUserRoleRequestV1>;

/** `PUT /console-users/{id}/permissions`: individual grants added on top of the role. */
export const GrantConsoleUserPermissionsRequestV1 = z
  .object({
    operator_id: z.uuid(),
    permissions: z.array(z.literal("location.precise.read")).min(1).max(1),
  })
  .strict()
  .meta({ id: "GrantConsoleUserPermissionsRequestV1" });
export type GrantConsoleUserPermissionsRequestV1 = z.infer<
  typeof GrantConsoleUserPermissionsRequestV1
>;

/** `POST /console-users/{id}/deactivate`: terminal; every session of the user was ended. */
export const DeactivatedConsoleUserResponseV1 = z
  .object({ console_user_id: z.uuid(), sessions_ended: z.int().min(0) })
  .strict()
  .meta({ id: "DeactivatedConsoleUserResponseV1" });
export type DeactivatedConsoleUserResponseV1 = z.infer<typeof DeactivatedConsoleUserResponseV1>;

export const InvitationSummaryV1 = z
  .object({
    invitation_id: z.uuid(),
    kind: ConsoleInvitationKindV1,
    email: z.email(),
    role: ConsoleRole.nullable(),
    status: z.enum(["pending", "accepted", "expired", "revoked"]),
    created_at: IsoDateTime,
    expires_at: IsoDateTime,
  })
  .strict()
  .meta({ id: "InvitationSummaryV1" });
export type InvitationSummaryV1 = z.infer<typeof InvitationSummaryV1>;

/** `GET /console-users/invitations`: never returns tokens or their digests. */
export const InvitationListResponseV1 = z
  .object({ invitations: z.array(InvitationSummaryV1) })
  .strict()
  .meta({ id: "InvitationListResponseV1" });
export type InvitationListResponseV1 = z.infer<typeof InvitationListResponseV1>;
